import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

function TCiudad() {
    const [ciudades, setCiudades] = useState([]);
    const [ciudadesFiltradas, setCiudadesFiltradas] = useState([]);
    const [paises, setPaises] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [formulario, setFormulario] = useState({
        idPais: '',
        idCiudad: '',
        nomCiudad: '',
        idZonav: 0
    });

    // Estados para el selector de país
    const [paisSeleccionado, setPaisSeleccionado] = useState(null);
    const [dropdownPaisAbierto, setDropdownPaisAbierto] = useState(false);
    const [busquedaPais, setBusquedaPais] = useState('');
    const dropdownPaisRef = useRef(null);

    // Paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(10);

    const API_CIUDADES_URL = '/api/ciudades';
    const API_PAISES_URL = '/api/paises';

    useEffect(() => {
        cargarCiudades();
        cargarPaises();
    }, []);

    useEffect(() => {
        filtrarCiudades();
    }, [busqueda, ciudades]);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownPaisRef.current && !dropdownPaisRef.current.contains(event.target)) {
                setDropdownPaisAbierto(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarCiudades = async () => {
        try {
            const response = await axios.get(API_CIUDADES_URL);
            setCiudades(response.data);
            setCiudadesFiltradas(response.data);
        } catch (error) {
            console.error('Error al cargar ciudades:', error);
            alert('Error al cargar las ciudades');
        }
    };

    const cargarPaises = async () => {
        try {
            const response = await axios.get(API_PAISES_URL);
            setPaises(response.data);
        } catch (error) {
            console.error('Error al cargar países:', error);
        }
    };

    const filtrarCiudades = () => {
        if (!busqueda) {
            setCiudadesFiltradas(ciudades);
            setPaginaActual(1);
            return;
        }

        const filtrados = ciudades.filter(ciudad =>
            ciudad.idPais?.toLowerCase().includes(busqueda.toLowerCase()) ||
            ciudad.idCiudad?.toString().includes(busqueda) ||
            ciudad.nomCiudad?.toLowerCase().includes(busqueda.toLowerCase()) ||
            ciudad.nomPais?.toLowerCase().includes(busqueda.toLowerCase())
        );
        setCiudadesFiltradas(filtrados);
        setPaginaActual(1);
    };

    // Funciones del selector de país
    const filtrarPaises = (busqueda) => {
        if (!busqueda) return paises;
        return paises.filter(pais =>
            pais.idPais?.toLowerCase().includes(busqueda.toLowerCase()) ||
            pais.nomPais?.toLowerCase().includes(busqueda.toLowerCase())
        );
    };

    const seleccionarPais = (pais) => {
        setPaisSeleccionado(pais);
        setFormulario({ ...formulario, idPais: pais.idPais });
        setBusquedaPais('');
        setDropdownPaisAbierto(false);
    };

    const limpiarPais = () => {
        setPaisSeleccionado(null);
        setFormulario({ ...formulario, idPais: '' });
        setBusquedaPais('');
    };

    const abrirModalNuevo = () => {
        setFormulario({
            idPais: '',
            idCiudad: '',
            nomCiudad: '',
            idZonav: 0
        });
        setPaisSeleccionado(null);
        setBusquedaPais('');
        setModoEdicion(false);
        setModalAbierto(true);
    };

    const abrirModalEditar = (ciudad) => {
        setFormulario({
            idPais: ciudad.idPais,
            idCiudad: ciudad.idCiudad,
            nomCiudad: ciudad.nomCiudad,
            idZonav: ciudad.idZonav
        });
        // Buscar y setear el país seleccionado
        const pais = paises.find(p => p.idPais === ciudad.idPais);
        if (pais) {
            setPaisSeleccionado(pais);
        }
        setModoEdicion(true);
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setFormulario({
            idPais: '',
            idCiudad: '',
            nomCiudad: '',
            idZonav: 0
        });
        setPaisSeleccionado(null);
        setBusquedaPais('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formulario.idPais || !formulario.idCiudad || !formulario.nomCiudad.trim()) {
            alert('Todos los campos son requeridos');
            return;
        }

        try {
            if (modoEdicion) {
                await axios.put(`${API_CIUDADES_URL}/${formulario.idPais}/${formulario.idCiudad}`, formulario);
                alert('Ciudad actualizada exitosamente');
            } else {
                await axios.post(API_CIUDADES_URL, formulario);
                alert('Ciudad creada exitosamente');
            }
            cargarCiudades();
            cerrarModal();
        } catch (error) {
            console.error('Error al guardar:', error);
            alert('Error al guardar la ciudad');
        }
    };

    const eliminar = async (idPais, idCiudad) => {
        if (window.confirm('¿Está seguro de eliminar esta ciudad?')) {
            try {
                await axios.delete(`${API_CIUDADES_URL}/${idPais}/${idCiudad}`);
                alert('Ciudad eliminada exitosamente');
                cargarCiudades();
            } catch (error) {
                console.error('Error al eliminar:', error);
                alert('Error al eliminar la ciudad');
            }
        }
    };

    const exportarExcel = () => {
        const datosExportar = ciudadesFiltradas.map(ciudad => ({
            'País': ciudad.idPais,
            'Nombre País': ciudad.nomPais,
            'ID Ciudad': ciudad.idCiudad,
            'Nombre Ciudad': ciudad.nomCiudad,
            'ID Zona V': ciudad.idZonav
        }));

        const worksheet = XLSX.utils.json_to_sheet(datosExportar);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Ciudades');
        XLSX.writeFile(workbook, 'ciudades.xlsx');
    };

    // Paginación
    const indiceUltimo = paginaActual * itemsPorPagina;
    const indicePrimero = indiceUltimo - itemsPorPagina;
    const ciudadesActuales = ciudadesFiltradas.slice(indicePrimero, indiceUltimo);
    const totalPaginas = Math.ceil(ciudadesFiltradas.length / itemsPorPagina);

    const cambiarPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
    };

    const cambiarItemsPorPagina = (e) => {
        setItemsPorPagina(Number(e.target.value));
        setPaginaActual(1);
    };

    return (
        <div className="present-container">
            <div className="header-seccion">
                <h2>Gestión de Ciudades</h2>
                <div className="acciones-header">
                    <input
                        type="text"
                        className="input-busqueda"
                        placeholder="Buscar..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>
                        ➕ Nueva Ciudad
                    </button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>
                        📊 Exportar a Excel
                    </button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>País</th>
                            <th>Nombre País</th>
                            <th>ID Ciudad</th>
                            <th>Nombre Ciudad</th>
                            <th>ID Zona V</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ciudadesActuales.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="sin-datos">No hay ciudades para mostrar</td>
                            </tr>
                        ) : (
                            ciudadesActuales.map(ciudad => (
                                <tr key={`${ciudad.idPais}-${ciudad.idCiudad}`}>
                                    <td>{ciudad.idPais}</td>
                                    <td>{ciudad.nomPais}</td>
                                    <td>{ciudad.idCiudad}</td>
                                    <td>{ciudad.nomCiudad}</td>
                                    <td>{ciudad.idZonav}</td>
                                    <td>
                                        <button
                                            onClick={() => abrirModalEditar(ciudad)}
                                            className="btn-accion"
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => eliminar(ciudad.idPais, ciudad.idCiudad)}
                                            className="btn-accion"
                                            title="Eliminar"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="paginacion-container">
                <div className="paginacion-info">
                    <span>
                        Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, ciudadesFiltradas.length)} de {ciudadesFiltradas.length} registros
                    </span>
                    <select
                        value={itemsPorPagina}
                        onChange={cambiarItemsPorPagina}
                        className="select-registros"
                    >
                        <option value={5}>5 por página</option>
                        <option value={10}>10 por página</option>
                        <option value={20}>20 por página</option>
                        <option value={50}>50 por página</option>
                    </select>
                </div>

                <div className="paginacion-controles">
                    <button
                        onClick={() => cambiarPagina(1)}
                        disabled={paginaActual === 1}
                        className="btn-paginacion"
                        title="Primera página"
                    >
                        ⏮️
                    </button>
                    <button
                        onClick={() => cambiarPagina(paginaActual - 1)}
                        disabled={paginaActual === 1}
                        className="btn-paginacion"
                    >
                        ◀️
                    </button>

                    {[...Array(totalPaginas)].map((_, index) => {
                        const numeroPagina = index + 1;
                        if (
                            numeroPagina === 1 ||
                            numeroPagina === totalPaginas ||
                            (numeroPagina >= paginaActual - 1 && numeroPagina <= paginaActual + 1)
                        ) {
                            return (
                                <button
                                    key={numeroPagina}
                                    onClick={() => cambiarPagina(numeroPagina)}
                                    className={`btn-numero-pagina ${paginaActual === numeroPagina ? 'activo' : ''}`}
                                >
                                    {numeroPagina}
                                </button>
                            );
                        } else if (
                            numeroPagina === paginaActual - 2 ||
                            numeroPagina === paginaActual + 2
                        ) {
                            return <span key={numeroPagina}>...</span>;
                        }
                        return null;
                    })}

                    <button
                        onClick={() => cambiarPagina(paginaActual + 1)}
                        disabled={paginaActual === totalPaginas}
                        className="btn-paginacion"
                    >
                        ▶️
                    </button>
                    <button
                        onClick={() => cambiarPagina(totalPaginas)}
                        disabled={paginaActual === totalPaginas}
                        className="btn-paginacion"
                        title="Última página"
                    >
                        ⏭️
                    </button>
                </div>
            </div>

            {/* Modal */}
            {modalAbierto && (
                <div className="modal-overlay">
                    <div className="modal-contenido">
                        <div className="modal-header">
                            <h3>{modoEdicion ? 'Editar Ciudad' : 'Nueva Ciudad'}</h3>
                            <button onClick={cerrarModal} className="btn-cerrar">×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>País *</label>
                                <div className="cuenta-selector" ref={dropdownPaisRef}>
                                    {paisSeleccionado ? (
                                        <div className="cuenta-seleccionada">
                                            <span>{paisSeleccionado.idPais} - {paisSeleccionado.nomPais}</span>
                                            {!modoEdicion && (
                                                <button type="button" onClick={limpiarPais} className="btn-limpiar">✕</button>
                                            )}
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            placeholder="Buscar país..."
                                            value={busquedaPais}
                                            onChange={(e) => setBusquedaPais(e.target.value)}
                                            onFocus={() => setDropdownPaisAbierto(true)}
                                            className="input-busqueda"
                                            disabled={modoEdicion}
                                        />
                                    )}

                                    {dropdownPaisAbierto && !paisSeleccionado && !modoEdicion && (
                                        <div className="cuenta-dropdown">
                                            {filtrarPaises(busquedaPais).map(pais => (
                                                <div
                                                    key={pais.idPais}
                                                    className="cuenta-item"
                                                    onClick={() => seleccionarPais(pais)}
                                                >
                                                    <strong>{pais.idPais}</strong> - {pais.nomPais}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>ID Ciudad *</label>
                                <input
                                    type="number"
                                    value={formulario.idCiudad}
                                    onChange={(e) => setFormulario({ ...formulario, idCiudad: parseInt(e.target.value) || '' })}
                                    required
                                    placeholder="Ingrese el ID de la ciudad"
                                    disabled={modoEdicion}
                                />
                            </div>

                            <div className="form-group">
                                <label>Nombre de la Ciudad *</label>
                                <input
                                    type="text"
                                    value={formulario.nomCiudad}
                                    onChange={(e) => setFormulario({ ...formulario, nomCiudad: e.target.value })}
                                    required
                                    maxLength={15}
                                    placeholder="Ingrese el nombre de la ciudad"
                                />
                            </div>

                            <div className="form-group">
                                <label>ID Zona V</label>
                                <input
                                    type="number"
                                    value={formulario.idZonav}
                                    onChange={(e) => setFormulario({ ...formulario, idZonav: parseInt(e.target.value) || 0 })}
                                    placeholder="Ingrese el ID de zona (default: 0)"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-guardar">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TCiudad;
